import React, {useState} from 'react';
import {Grid} from '@mui/material';
import {useAccountContext} from '../../contexts/AccountContext';
import {useAmlKyc} from '../../features/amlKyc/useAmlKyc';
import {AmlKycTable} from '../../features/amlKyc/AmlKycTable';
import {AmlKycModal} from '../../features/amlKyc/AmlKycModal';
import {AmlKycMissingPrompt} from '../../features/amlKyc/AmlKycMissingPrompt';

/**
 * Account overview "AML/KYC" panel (DBO-1584): one row per primary/secondary
 * contact, mirroring what Curo holds. Each save syncs to Curo at once, so the
 * mutation's returned contact updates the cached row without a refetch.
 */
export const AccountAmlKyc: React.FC = () => {
  const {account, accountId} = useAccountContext();
  const holderIds = [account?.primaryContact?.id, account?.secondaryContact?.id].filter(
    (id): id is string => Boolean(id)
  );
  const {loading, options, contacts} = useAmlKyc(accountId, holderIds);
  const [editingContactId, setEditingContactId] = useState<string | null>(null);
  const [promptDismissed, setPromptDismissed] = useState(false);

  if (loading || !options?.isEnabled || contacts.length === 0 || !accountId) {
    return null;
  }
  const editing = contacts.find((contact) => contact.id === editingContactId);
  const contactsWithGaps = contacts.filter(
    (contact) => contact.amlKycMissingFields.length > 0
  );
  const promptOpen =
    !promptDismissed && editingContactId === null && contactsWithGaps.length > 0;

  return (
    <Grid item xs={12}>
      <AmlKycTable
        contacts={contacts}
        nationalities={options.nationalities}
        onEdit={setEditingContactId}
      />
      <AmlKycMissingPrompt
        open={promptOpen}
        contacts={contactsWithGaps}
        onClose={() => setPromptDismissed(true)}
        onAddInformation={setEditingContactId}
      />
      {editing ? (
        <AmlKycModal
          open
          onClose={() => setEditingContactId(null)}
          onSaved={() => undefined}
          accountId={accountId}
          contact={editing}
          options={options}
        />
      ) : null}
    </Grid>
  );
};
